import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, CalendarDays, Mail, Sparkles } from "lucide-react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import FloatingWhatsApp from "@/components/FloatingWhatsApp";
import useReveal from "@/hooks/useReveal";
import { api } from "@/lib/api";
import { trackPageView, track } from "@/lib/analytics";

export default function PlansIndex() {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const r = useReveal();

  useEffect(() => {
    trackPageView();
    api
      .get("/plans")
      .then(({ data }) => setPlans(data || []))
      .catch(() => setPlans([]))
      .finally(() => setLoading(false));
  }, []);

  const featured = plans[0];
  const others = plans.slice(1);

  return (
    <div data-testid="plans-index-page" className="bg-[#FAFAFA] min-h-screen">
      <Navbar />
      <main className="pt-32 pb-24">
        <section className="max-w-7xl mx-auto px-6 md:px-8">
          <div ref={r} className="reveal max-w-3xl">
            <div className="flex items-center gap-3 mb-6">
              <span className="h-px w-10 bg-[#C5A059]" aria-hidden />
              <span className="text-[0.7rem] uppercase tracking-[0.3em] text-[#C5A059] font-medium">
                Planos de Estudo
              </span>
            </div>
            <h1
              data-testid="plans-index-title"
              className="font-serif-display text-[#0B1E36] text-4xl md:text-6xl lg:text-[5rem] leading-[1.0] tracking-[-0.032em] font-medium"
            >
              Comece uma jornada{" "}
              <span className="italic text-[#C5A059]">com Deus hoje</span>.
            </h1>
            <p
              data-testid="plans-index-subtitle"
              className="mt-8 text-[1.05rem] md:text-[1.2rem] text-[#6B7280] leading-[1.85] font-light"
            >
              Planos guiados para transformar sua vida espiritual dia após dia.
              Versículo, reflexão e aplicação prática enviados ao seu e-mail,
              no seu ritmo.
            </p>
          </div>

          {loading ? (
            <div className="mt-20 grid lg:grid-cols-12 gap-8">
              <div className="lg:col-span-7 aspect-[4/3] bg-[#F5F7FA] animate-pulse" />
              <div className="lg:col-span-5 grid gap-6">
                {Array.from({ length: 3 }).map((_, i) => (
                  <div
                    key={i}
                    className="aspect-[5/2] bg-[#F5F7FA] animate-pulse"
                  />
                ))}
              </div>
            </div>
          ) : plans.length === 0 ? (
            <p data-testid="plans-empty" className="mt-16 text-[#6B7280]">
              Nenhum plano disponível ainda.
            </p>
          ) : (
            <>
              {/* Featured plan + 3 outros */}
              {featured && (
                <FeaturedPlanCard plan={featured} />
              )}

              {others.length > 0 && (
                <div
                  data-testid="plans-list"
                  className="mt-16 grid md:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-12"
                >
                  {others.map((p) => (
                    <PlanCard key={p.id} plan={p} />
                  ))}
                </div>
              )}
            </>
          )}
        </section>
      </main>
      <Footer />
      <FloatingWhatsApp />
    </div>
  );
}

function FeaturedPlanCard({ plan }) {
  return (
    <Link
      to={`/planos/${plan.slug}`}
      data-testid={`plan-card-${plan.slug}`}
      onClick={() => track("click_plan", { slug: plan.slug, position: "featured" })}
      className="mt-20 block group"
    >
      <article className="relative grid lg:grid-cols-12 gap-0 bg-[#0B1E36] text-white overflow-hidden border border-[#C5A059]/15">
        {/* Lado esquerdo: capa visual */}
        <div className="lg:col-span-5 relative min-h-[340px] md:min-h-[420px] lg:min-h-[520px]">
          <div
            className="absolute inset-0"
            style={{
              background:
                "radial-gradient(ellipse at 30% 25%, rgba(197,160,89,0.32) 0%, transparent 60%), linear-gradient(155deg, #0B1E36 0%, #13294B 60%, #0B1E36 100%)",
            }}
            aria-hidden
          />
          <div className="absolute inset-6 lg:inset-8 border border-[#C5A059]/25" />
          <div className="relative h-full flex flex-col p-8 lg:p-12">
            <div className="flex items-center justify-between">
              <span className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.32em] text-[#C5A059] font-medium">
                <Sparkles size={11} strokeWidth={1.6} />
                Plano em destaque
              </span>
              <span className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.3em] text-white/55">
                <CalendarDays size={11} strokeWidth={1.6} />
                {plan.duration_days} dias
              </span>
            </div>
            <div className="mt-auto">
              <span className="font-serif-display text-[5rem] md:text-[6rem] text-[#C5A059] leading-none block">
                {String(plan.plan_order || 1).padStart(2, "0")}
              </span>
            </div>
          </div>
        </div>

        {/* Lado direito: conteúdo editorial */}
        <div className="lg:col-span-7 p-8 md:p-12 lg:p-16 flex flex-col justify-between gap-10 bg-[#0B1E36]">
          <div>
            <p className="text-[0.65rem] uppercase tracking-[0.28em] text-[#C5A059] font-medium mb-5">
              Plano de {plan.duration_days} dias · {plan.category || "Família"}
            </p>
            <h2
              data-testid={`plan-card-${plan.slug}-title`}
              className="font-serif-display text-white text-[2rem] md:text-[2.6rem] lg:text-[3.2rem] leading-[1.05] tracking-[-0.024em]"
            >
              {plan.title}
            </h2>
            {plan.subtitle && (
              <p className="mt-5 font-serif-display italic text-white/75 text-[1.1rem] md:text-[1.35rem] leading-[1.45]">
                {plan.subtitle}
              </p>
            )}
            <p
              data-testid={`plan-card-${plan.slug}-description`}
              className="mt-7 text-white/65 leading-[1.85] font-light text-[0.98rem] max-w-2xl"
            >
              {plan.description}
            </p>
          </div>

          <div className="flex flex-wrap items-center gap-x-8 gap-y-4">
            <span
              data-testid={`plan-card-${plan.slug}-cta`}
              className="inline-flex items-center gap-3 bg-[#C5A059] hover:bg-[#B58F4A] text-[#0B1E36] px-7 py-4 text-xs uppercase tracking-[0.26em] font-medium transition-colors"
            >
              Começar jornada
              <ArrowUpRight
                size={14}
                strokeWidth={1.7}
                className="transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              />
            </span>
            {plan.subscriber_count > 0 && (
              <span className="text-[0.7rem] uppercase tracking-[0.24em] text-white/55">
                {plan.subscriber_count}{" "}
                {plan.subscriber_count === 1 ? "pessoa inscrita" : "pessoas inscritas"}
              </span>
            )}
            <span className="inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.24em] text-white/45">
              <Mail size={12} strokeWidth={1.6} />
              Entrega por e-mail
            </span>
          </div>
        </div>
      </article>
    </Link>
  );
}

function PlanCard({ plan }) {
  return (
    <Link
      to={`/planos/${plan.slug}`}
      data-testid={`plan-card-${plan.slug}`}
      onClick={() => track("click_plan", { slug: plan.slug, position: "grid" })}
      className="group block"
    >
      <div className="relative aspect-[4/5] bg-[#0B1E36] overflow-hidden">
        <div
          className="absolute inset-0"
          style={{
            background:
              "radial-gradient(ellipse at 70% 20%, rgba(197,160,89,0.22) 0%, transparent 60%), linear-gradient(165deg, #0B1E36 0%, #13294B 60%, #0B1E36 100%)",
          }}
          aria-hidden
        />
        <div className="absolute inset-5 border border-[#C5A059]/20" />
        <div className="relative h-full flex flex-col p-8">
          <div className="flex items-center justify-between">
            <span className="font-serif-display text-[2.4rem] text-[#C5A059] leading-none">
              {String(plan.plan_order || 1).padStart(2, "0")}
            </span>
            <span className="inline-flex items-center gap-2 text-[0.62rem] uppercase tracking-[0.3em] text-white/55">
              <CalendarDays size={11} strokeWidth={1.6} />
              {plan.duration_days} dias
            </span>
          </div>
          <div className="mt-auto">
            <p className="text-[0.62rem] uppercase tracking-[0.28em] text-[#C5A059] font-medium mb-3">
              Plano de {plan.duration_days} dias
            </p>
            <h3
              data-testid={`plan-card-${plan.slug}-title`}
              className="font-serif-display italic text-white text-[1.4rem] md:text-[1.55rem] leading-[1.2] tracking-[-0.012em]"
            >
              {plan.title}
            </h3>
          </div>
        </div>
      </div>
      <p
        data-testid={`plan-card-${plan.slug}-description`}
        className="mt-5 text-[#6B7280] text-[0.95rem] leading-[1.7] font-light line-clamp-3"
      >
        {plan.description}
      </p>
      <span
        data-testid={`plan-card-${plan.slug}-cta`}
        className="mt-4 inline-flex items-center gap-2 text-[0.7rem] uppercase tracking-[0.24em] text-[#0B1E36] group-hover:text-[#C5A059] transition-colors duration-300 border-b border-[#0B1E36]/15 group-hover:border-[#C5A059]/60 pb-1.5 font-medium"
      >
        Começar jornada
        <ArrowUpRight
          size={12}
          strokeWidth={1.7}
          className="opacity-70 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
        />
      </span>
    </Link>
  );
}
